import { api } from "./api";
import { ListRequest, MessageResponse } from "../types/common/commonTypes";
import {
  UserFull,
  UserCreate,
  UserUpdate,
  PaginatedUserResponse,
} from "../types/userTypes";

/**
 * Inventory Management Service
 *
 * Provides complete integration with all 12 inventory management API endpoints:
 * - Core lot management (CRUD operations)
 * - FIFO allocation engine
 * - Real-time availability tracking
 * - Lot shipping and movement
 * - Comprehensive inventory summaries
 *
 * Auto-generation: Lot numbers are auto-generated by backend (YY-###### format)
 * Example: 24-000001, 24-000002, etc. (resets annually)
 */
const user_base = "/users";
export const userService = {
  // ========================================
  // Core Inventory Lot Management (6 endpoints)
  // ========================================

  /**
   * Create a new inventory lot
   * POST /api/v1/inventory/lots
   * Note: Lot numbers are auto-generated by backend (format: YY-######)
   */
  create: async (data: UserCreate): Promise<UserFull> => {
    const response = await api.post(`${user_base}/`, data);
    return response.data;
  },
  list: async (params: ListRequest): Promise<PaginatedUserResponse> => {
    const response = await api.post(`${user_base}/search`, params);
    return response.data;
  },

  /**
   * Get a specific inventory lot by ID
   * GET /api/v1/inventory/lots/{lot_id}
   */
  getById: async (id: string): Promise<UserFull> => {
    const response = await api.get(`${user_base}/${id}`);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  update: async (id: string, data: UserUpdate): Promise<UserFull> => {
    const response = await api.put(`${user_base}/${id}`, { ...data, id });
    return response.data;
  },

  /**
   * Delete an inventory lot (soft delete)
   * DELETE /api/v1/inventory/lots/{lot_id}
   */
  deactivate: async (id: string): Promise<MessageResponse> => {
    const response = await api.delete(`${user_base}/${id}`);
    return response.data;
  },

  /**
   * Restore a deleted inventory lot
   * POST /api/v1/inventory/lots/{lot_id}/restore
   */
  reactivate: async (id: string): Promise<UserFull> => {
    const response = await api.post(`${user_base}/${id}/restore`);
    return response.data;
  },
};
